import React from "react";
import { Link } from "react-router-dom";
import StatusBadge from "../../../../components/common/StatusBadge";
import EmployeeAvatar from "../../../reports/oneStop/components/EmployeeAvatar";
import {
  PETTY_CASH_VOUCHER_STATUS_CLASS,
  PETTY_CASH_VOUCHER_STATUS_LABEL,
  PETTY_CASH_RECEIPT_STATUS_CLASS,
  PETTY_CASH_RECEIPT_STATUS_LABEL,
} from "../../pettyCashStatusMaps";
import { paths } from "../../../../routes/paths";
import { payeeName, voucherNet } from "../../reports/voucherReport";

// Thousands separators on screen; exports keep the plain 2-decimal format.
export const amt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortDate = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const Payee = ({ name, guid, photoUrls }) =>
  name ? (
    <div className="d-flex align-items-center gap-2">
      <EmployeeAvatar name={name} photoUrl={guid ? photoUrls[guid] : null} />
      <span className="text-truncate" style={{ maxWidth: 170 }} title={name}>
        {name}
      </span>
    </div>
  ) : (
    <span className="text-muted">-</span>
  );

/**
 * Voucher Register rows for the Overview - one line per voucher (a long
 * description is cut with "..." and shown whole on hover), so the section
 * and the full-screen pages can count on equal row heights. The voucher
 * number opens the voucher in a new tab, leaving this page as it is.
 */
export const RegisterTable = ({ rows, totals, showBranch, photoUrls }) => (
  <table className="table table-sm table-hover pc-table mb-0">
    <thead>
      <tr>
        <th>Voucher</th>
        <th>Date</th>
        {showBranch && <th>Branch</th>}
        <th>Category</th>
        <th>Description</th>
        <th>Paid To</th>
        <th className="text-end">Requested</th>
        <th className="text-end">Paid</th>
        <th className="text-end">Returned</th>
        <th className="text-end">Net</th>
        <th>Status</th>
        <th>Receipt</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => {
        const net = voucherNet(r);
        return (
          <tr key={r.recordGuid}>
            <td>
              <Link to={paths.pettyCashVoucherDetail(r.recordGuid)} target="_blank" rel="noopener noreferrer" title="Open in a new tab">
                {r.voucherNumber}
              </Link>
            </td>
            <td>{shortDate(r.expenseDate)}</td>
            {showBranch && <td>{r.branchCode}</td>}
            <td>{r.categoryName || "-"}</td>
            <td>
              <div className="pc-ellipsis" title={r.description || undefined}>
                {r.description || "-"}
              </div>
            </td>
            <td>
              <Payee name={payeeName(r)} guid={r.paidToEmployeeRecordGuid} photoUrls={photoUrls} />
            </td>
            <td className="text-end">{amt(r.requestedAmount)}</td>
            <td className="text-end">{r.paidAmount == null ? "-" : amt(r.paidAmount)}</td>
            <td className="text-end">{Number(r.returnedAmount || 0) > 0 ? <span className="text-success">{amt(r.returnedAmount)}</span> : <span className="text-muted">-</span>}</td>
            <td className="text-end fw-semibold">{net == null ? "-" : amt(net)}</td>
            <td>
              <StatusBadge status={r.status} classMap={PETTY_CASH_VOUCHER_STATUS_CLASS} labelMap={PETTY_CASH_VOUCHER_STATUS_LABEL} />
            </td>
            <td>
              {r.receiptStatus && r.receiptStatus !== "NOT_REQUIRED" ? (
                <StatusBadge status={r.receiptStatus} classMap={PETTY_CASH_RECEIPT_STATUS_CLASS} labelMap={PETTY_CASH_RECEIPT_STATUS_LABEL} />
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
    {totals && (
      <tfoot>
        <tr>
          <td colSpan={showBranch ? 6 : 5} className="fw-bold">
            Total &middot; {totals.count} voucher{totals.count === 1 ? "" : "s"}
          </td>
          <td className="text-end fw-bold">{amt(totals.requested)}</td>
          <td className="text-end fw-bold">{amt(totals.paid)}</td>
          <td className="text-end fw-bold">{amt(totals.returned)}</td>
          <td className="text-end fw-bold">{amt(totals.paid - totals.returned)}</td>
          <td colSpan={2} />
        </tr>
      </tfoot>
    )}
  </table>
);

/**
 * Grouped totals. Clicking a Category, Paid To or Fund row narrows the whole
 * page to it - the other two sections follow at once. The bar shows each
 * group's share of the net spend.
 */
export const SummaryTable = ({ rows, groupLabel, groupBy, grandTotals, onPick, photoUrls, maxNet: maxNetOfAll }) => {
  // Bars are scaled against every group, not just the rows on a full-screen page.
  const maxNet = maxNetOfAll ?? Math.max(0, ...rows.map((s) => s.net));
  return (
    <table className="table table-sm table-hover pc-table mb-0">
      <thead>
        <tr>
          <th>{groupLabel}</th>
          <th className="text-end">Vouchers</th>
          <th className="text-end">Requested</th>
          <th className="text-end">Paid</th>
          <th className="text-end">Paid Amount</th>
          <th className="text-end">Returned</th>
          <th className="text-end">Net Spend</th>
          <th style={{ width: "18%" }}>Share</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => {
          const canPick = onPick && s.filterValue != null;
          const share = grandTotals.net > 0 ? (s.net / grandTotals.net) * 100 : 0;
          return (
            <tr
              key={s.key}
              role={canPick ? "button" : undefined}
              onClick={canPick ? () => onPick(s) : undefined}
              title={canPick ? `Show only ${s.label} on this page` : undefined}
            >
              <td className="fw-semibold">
                {groupBy === "payee" && s.key.startsWith("e:") ? (
                  <Payee name={s.label} guid={s.key.slice(2)} photoUrls={photoUrls} />
                ) : (
                  <span className="pc-ellipsis d-inline-block align-middle" style={{ maxWidth: 260 }} title={s.label}>
                    {s.label}
                  </span>
                )}
              </td>
              <td className="text-end">{s.totalCount}</td>
              <td className="text-end">{amt(s.totalAmount)}</td>
              <td className="text-end">{s.paidCount}</td>
              <td className="text-end">{amt(s.paidAmount)}</td>
              <td className="text-end">{amt(s.returnedAmount)}</td>
              <td className="text-end fw-bold">{amt(s.net)}</td>
              <td>
                <div className="d-flex align-items-center gap-2">
                  <div className="pc-share-track">
                    <div className="pc-share-bar" style={{ width: `${maxNet > 0 ? Math.max(0, (s.net / maxNet) * 100) : 0}%` }} />
                  </div>
                  <small className="text-muted" style={{ width: 42 }}>
                    {share.toFixed(1)}%
                  </small>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td className="fw-bold">Total</td>
          <td className="text-end fw-bold">{grandTotals.count}</td>
          <td className="text-end fw-bold">{amt(grandTotals.requested)}</td>
          <td className="text-end fw-bold">{grandTotals.paidCount}</td>
          <td className="text-end fw-bold">{amt(grandTotals.paid)}</td>
          <td className="text-end fw-bold">{amt(grandTotals.returned)}</td>
          <td className="text-end fw-bold">{amt(grandTotals.net)}</td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
};
